import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';

import type {
  FullReferrerQuery,
  FullReferrerQueryVariables,
} from './__generated__/useEnterCode.graphql.ts';

type PromoCodeConfig = {
  code: string;
  referrer: string;
  utmCampaign: string;
};

const FULL_REFERRER_QUERY = gql`
  query FullReferrerQuery($referrerCode: String!) {
    fullReferrer(referrerCode: $referrerCode) {
      ... on User {
        slug
      }
      ... on UserSource {
        id
      }
    }
  }
` as TypedDocumentNode<FullReferrerQuery, FullReferrerQueryVariables>;

export const useEnterCode = () => {
  const [error, setError] = useState<string | undefined>();
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();
  const [utmCampaign] = useLocalStorage('UTM_CAMPAIGN', undefined);
  const [referrerCode] = useLocalStorage('REFERRER_CODE', '');
  const [inputValue, setInputValueRaw] = useState(referrerCode);

  const { formatMessage } = useIntl();
  const {
    flags: { promoCodesConfig },
  } = useFeatureFlags();
  const [fetchReferrer] = useLazyQuery(FULL_REFERRER_QUERY);

  const promoCodes: PromoCodeConfig[] = promoCodesConfig?.promoCodes ?? [];

  const setInputValue = (val: string) => {
    setError(undefined);
    setInputValueRaw(val);
  };

  // Returns true if code was valid or empty, false if invalid
  const validateAndApplyCode = async (): Promise<boolean> => {
    const trimmed = inputValue.trim();
    if (!trimmed) return true;

    const promoCodeConfig = promoCodes.find(p => p.code === trimmed);
    if (promoCodeConfig) {
      setSearchParams(
        prev => {
          prev.set('utm_campaign', promoCodeConfig.utmCampaign);
          prev.set('referrer_code', promoCodeConfig.code);
          prev.set('referrer', promoCodeConfig.referrer);
          prev.set('action', 'signup');
          return prev;
        },
        { replace: true }
      );
      return true;
    }

    const { data } = await fetchReferrer({
      variables: { referrerCode: trimmed },
    });
    if (data?.fullReferrer) {
      setSearchParams(
        prev => {
          prev.set('referrer_code', trimmed);
          prev.set('action', 'signup');
          return prev;
        },
        { replace: true }
      );
      return true;
    }

    setError(
      formatMessage({
        defaultMessage: 'Sorry, this code is incorrect or expired',
      })
    );
    return false;
  };

  return {
    inputValue,
    setInputValue,
    error,
    successSnackbarOpen,
    setSuccessSnackbarOpen,
    validateAndApplyCode,
    // Hide code input if the referrer code is not persisted, only utm_campaign
    // (probably comes from an affiliate/ad link)
    shouldHideCodeInput: !!utmCampaign && !referrerCode,
  };
};
